/**
 * Sort state for the portal Orders list: the sort keys the backend accepts,
 * the default direction per column, the click-cycling rule and the URL
 * parameter guards. Pure, framework-free helpers kept out of
 * PortalOrdersPage.tsx so that file only exports components
 * (react-refresh/only-export-components) and so they can be unit-tested
 * without rendering (see PortalOrdersSearchSort.test.ts).
 */

// Sort keys supported by the backend /portal/orders endpoint
// (see services/orders/service.py SORT_COLUMNS).
export type OrdersSortKey =
  | 'created_at'
  | 'updated_at'
  | 'customer_name'
  | 'status'
  | 'subtotal';

export type OrdersSortDir = 'asc' | 'desc';

const SORT_KEYS: OrdersSortKey[] = [
  'created_at',
  'updated_at',
  'customer_name',
  'status',
  'subtotal',
];

/** Default direction when a column is first clicked. */
const DEFAULT_SORT_DIR: Record<OrdersSortKey, OrdersSortDir> = {
  created_at: 'desc',
  updated_at: 'desc',
  customer_name: 'asc',
  status: 'asc',
  subtotal: 'desc',
};

/**
 * Pure helper: compute the next (sortBy, sortDir) given the current state
 * and the column just clicked. Clicking the active column toggles
 * asc↔desc; clicking a different column switches to that column with
 * its default direction.
 */
export function nextSortState(
  current: { sortBy: OrdersSortKey; sortDir: OrdersSortDir },
  clicked: OrdersSortKey,
): { sortBy: OrdersSortKey; sortDir: OrdersSortDir } {
  if (current.sortBy === clicked) {
    return {
      sortBy: clicked,
      sortDir: current.sortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy: clicked, sortDir: DEFAULT_SORT_DIR[clicked] };
}

export function isSortKey(v: string | null): v is OrdersSortKey {
  return v !== null && (SORT_KEYS as string[]).includes(v);
}

export function isSortDir(v: string | null): v is OrdersSortDir {
  return v === 'asc' || v === 'desc';
}
